/**
 * Make a pipeline failure readable without opening anything else — which on
 * this deployment means without opening anything at all, because there is no
 * execution history to go and read. This string is the whole record.
 *
 * What is actually stored is not a message. The one failed episode in
 * production, `8a9ac7b6-…`, carries this as its `failureReason`:
 *
 *     {"errorMessage": "Parameter validation failed:\nInvalid leng…
 *
 * A raw boto3 traceback, JSON-encoded, then truncated to 500 characters by
 * `release_hold` — so it does not parse as JSON, and `JSON.parse` throws on it.
 * Rendering that string verbatim in a table cell is how an operator ends up
 * hunting for a workflow console this deployment does not have.
 *
 * So: try JSON, and when that fails (which is the common case, because of the
 * truncation) pull the message out with a regex and say plainly that the rest
 * was cut off. The raw text is always kept and always offered — a parser that
 * hides what it could not understand is worse than no parser.
 */
import { useState } from 'react';

export type Failure = {
  /** The one line worth putting in a table cell. */
  headline: string;
  errorType?: string;
  trace?: string[];
  raw: string;
  /** Whether the structure was recovered, or this is just the raw string. */
  parsed: boolean;
  /** True when the stored text was cut off mid-value. */
  truncated: boolean;
};

export function explainFailure(raw?: string | null): Failure | null {
  if (!raw) return null;
  const text = String(raw);

  try {
    const parsed: unknown = JSON.parse(text);
    if (parsed && typeof parsed === 'object') {
      const obj = parsed as Record<string, unknown>;
      const headline = str(obj.errorMessage) ?? str(obj.Cause) ?? str(obj.message) ?? text;
      const trace = Array.isArray(obj.stackTrace)
        ? obj.stackTrace.map(String)
        : Array.isArray(obj.trace)
          ? obj.trace.map(String)
          : undefined;
      return {
        headline: firstLine(headline),
        errorType: str(obj.errorType) ?? str(obj.Error),
        ...(trace ? { trace } : {}),
        raw: text,
        parsed: true,
        truncated: false,
      };
    }
  } catch {
    // Expected, not exceptional: release_hold truncates at 500 chars and the
    // cut almost always lands inside the JSON.
  }

  const message = /"errorMessage"\s*:\s*"((?:[^"\\]|\\.)*)/.exec(text);
  const type = /"errorType"\s*:\s*"([^"]*)"/.exec(text);
  if (message?.[1]) {
    return {
      headline: firstLine(unescape(message[1])),
      ...(type?.[1] ? { errorType: type[1] } : {}),
      raw: text,
      parsed: true,
      truncated: !text.trimEnd().endsWith('}'),
    };
  }

  return {
    headline: firstLine(text),
    raw: text,
    parsed: false,
    truncated: false,
  };
}

function str(value: unknown): string | undefined {
  return typeof value === 'string' && value ? value : undefined;
}

function firstLine(text: string): string {
  const line = text.split('\n').find((part) => part.trim());
  return (line ?? text).trim();
}

function unescape(fragment: string): string {
  try {
    return JSON.parse(`"${fragment}"`) as string;
  } catch {
    // Truncated mid-escape. Do the two that matter and leave the rest visible
    // rather than throwing away the message.
    return fragment.replace(/\\n/g, '\n').replace(/\\"/g, '"');
  }
}

/** The failure, rendered: one readable line, with the raw text one click away
 *  and never more than one click away. */
export function FailureBlock({ reason }: { reason?: string | null }) {
  const [open, setOpen] = useState(false);
  const failure = explainFailure(reason);
  if (!failure) return null;

  return (
    <div className="notice notice-bad">
      <b>{failure.headline}</b>
      {failure.errorType && <span className="mono small">{failure.errorType}</span>}
      {failure.truncated && (
        // THE RENDERED SENTENCE, which is the one an operator acts on. It said
        // "the pipeline's CloudWatch group, which never expires" — a service
        // this deployment does not use and a retention that is not what this
        // one has. The docstring above was rewritten during the Azure port and
        // this string was left behind, so the console was sending whoever read
        // it to another cloud's console to look for a traceback that ages out
        // of THIS one in a month.
        <span className="muted small">
          Stored truncated at 500 characters by <code>release_hold</code> — the rest
          of this traceback is in Application Insights (the{' '}
          <span className="mono">traces</span> table, on the Function App), which
          keeps 30 days and no longer. After that this line is the whole record.
        </span>
      )}
      {!failure.parsed && (
        <span className="muted small">
          Not a recognised error shape; showing it verbatim.
        </span>
      )}
      <button className="link" onClick={() => setOpen(!open)}>
        {open ? 'hide the raw reason' : 'show the raw reason'}
      </button>
      {open && <pre className="raw">{failure.raw}</pre>}
      {open && failure.trace?.length ? (
        <pre className="raw">{failure.trace.join('\n')}</pre>
      ) : null}
    </div>
  );
}
